import RemoveFieldIcon from "assets/images/RemoveFieldIcon";
import locationPinIcon from "assets/images/location-pin-small.svg";
import {useEffect, useMemo, useState} from "react";
import {common as stringsCommon, transferPharmacy as strings} from "strings";
import {v4 as uuidv4} from "uuid";

import Button from "components/Button";
import MobileNavigationHeader from "components/MobileNavigationHeader";
import PageTitle from "components/PageTitle";
import Select from "components/Select";
import StateInput from "components/StateInput";
import TextInput from "components/TextInput";

import {
  formatPhoneNumber,
  isValidCity,
  isValidPharmacyName,
  isValidTransferMedName,
} from "liberty-core/sharedFunctions";

import {useAuthenticatedUser} from "lib/auth";
import useCurrentPatientId from "lib/useCurrentPatientId";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./TransferPharmacyPage.module.css";

type Props = {
  rxNumberValue: string;
  onChangeText: (text: string) => void;
  onDelete: () => void;
};

function MutableRxInput({rxNumberValue, onChangeText, onDelete}: Props) {
  return (
    <div className={styles.rxRow}>
      <TextInput
        label={strings.medName}
        type="text"
        value={rxNumberValue}
        onChangeText={onChangeText}
        className={styles.input}
      />
      <button className={styles.removeFieldButton} onClick={onDelete}>
        <RemoveFieldIcon />
      </button>
    </div>
  );
}

export type RxNumber = {
  id: string | number[];
  value: string;
};

const DEFAULT_PATIENT_ID = "defaultPatientId";

export default function TransferPharmacyPage() {
  const [pharmacy] = useCurrentPharmacy();
  const [authenticatedUser] = useAuthenticatedUser();
  const {selectedPatientId, updateSelectedPatientId} = useCurrentPatientId(
    authenticatedUser?.data.linkedPatients ?? [],
  );

  const [pharmacyName, setPharmacyName] = useState(
    localStorage.getItem("pharmacy-name") || "",
  );
  const [pharmacyPhone, setPharmacyPhone] = useState(
    localStorage.getItem("pharmacy-phone") || "",
  );
  const [pharmacyCity, setPharmacyCity] = useState(
    localStorage.getItem("pharmacy-city") || "",
  );
  const [pharmacyState, setPharmacyState] = useState(
    localStorage.getItem("pharmacy-state") || "",
  );
  const [primaryRx, setPrimaryRx] = useState(
    localStorage.getItem("primary-rx") || "",
  );
  const [rxNumberList, setRxNumberList] = useState<RxNumber[]>(
    JSON.parse(localStorage.getItem("rx-number-list") || "[]") || [],
  );

  const [disabled, setDisabled] = useState(true);

  useEffect(() => {
    localStorage.setItem("pharmacy-name", pharmacyName);
    localStorage.setItem("pharmacy-phone", pharmacyPhone);
    localStorage.setItem("pharmacy-city", pharmacyCity);
    localStorage.setItem("pharmacy-state", pharmacyState);
    localStorage.setItem("primary-rx", primaryRx);
    localStorage.setItem("rx-number-list", JSON.stringify(rxNumberList));
    if (
      pharmacyName.trim().length > 0 &&
      pharmacyPhone.replace(/\D/g, "").trim().length === 10 &&
      pharmacyCity.trim().length > 0 &&
      pharmacyState.trim().length > 0 &&
      primaryRx.trim().length > 0
    ) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [
    pharmacyCity,
    pharmacyName,
    pharmacyPhone,
    pharmacyState,
    primaryRx,
    rxNumberList,
  ]);

  const patientNameOptions = useMemo(() => {
    if (!authenticatedUser?.data.linkedPatients?.length) {
      return [];
    }

    return [
      {
        value: DEFAULT_PATIENT_ID,
        label: authenticatedUser.data.name,
      },
      ...authenticatedUser.data.linkedPatients.map((p) => ({
        value: p.patientId,
        label: `${p.firstName} ${p.lastName}`,
      })),
    ].sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
  }, [authenticatedUser]);

  return (
    <div className={styles.container}>
      <MobileNavigationHeader backTo="/profile" />
      <PageTitle className={styles.title}>{strings.title}</PageTitle>
      <div>
        <img src={locationPinIcon} alt="" />
        <span className={styles.subtitle}>{pharmacy?.name}</span>
      </div>
      <h3 className={styles.description}>{strings.description}</h3>
      <Select
        className={styles.patientNameSelect}
        value={selectedPatientId ?? DEFAULT_PATIENT_ID}
        options={patientNameOptions}
        disabled={!authenticatedUser?.data.linkedPatients?.length}
        onChange={(v) =>
          updateSelectedPatientId(v === DEFAULT_PATIENT_ID ? null : v)
        }
      />
      <span className={styles.pharmacyInfo}>{strings.outgoing}</span>
      <TextInput
        label={strings.pharmName}
        type="text"
        value={pharmacyName}
        onChangeText={(val) => {
          if (isValidPharmacyName(val)) {
            setPharmacyName(val);
          }
        }}
        className={styles.input}
        maxLength={50}
      />
      <TextInput
        label={strings.pharmPhone}
        type="tel"
        value={pharmacyPhone}
        onChangeText={setPharmacyPhone}
        className={styles.input}
      />
      <TextInput
        label={strings.pharmCity}
        type="text"
        value={pharmacyCity}
        onChangeText={(val) => {
          if (isValidCity(val)) {
            setPharmacyCity(val);
          }
        }}
        className={styles.input}
        maxLength={100}
      />
      <StateInput
        value={pharmacyState}
        onChange={setPharmacyState}
        placeholder={strings.pharmState}
        className={styles.input}
      />
      <TextInput
        label={stringsCommon.rxNumber}
        placeholder={strings.medName}
        type="text"
        value={primaryRx}
        onChangeText={(val) => {
          if (isValidTransferMedName(val)) {
            setPrimaryRx(val);
          }
        }}
        className={styles.input}
        maxLength={50}
      />
      {rxNumberList.map((rxNumber, i) => {
        return (
          <MutableRxInput
            key={rxNumber.id.toString()}
            rxNumberValue={rxNumber.value}
            onChangeText={(newValue) => {
              if (isValidTransferMedName(newValue)) {
                const newArr = rxNumberList.slice();
                newArr[i].value = newValue;
                setRxNumberList(newArr);
              }
            }}
            onDelete={() => {
              setRxNumberList((prevList) =>
                prevList.filter((curNumber) => curNumber.id !== rxNumber.id),
              );
            }}
          />
        );
      })}
      <Button
        variant="minimal"
        className={styles.moreMeds}
        onClick={() => {
          setRxNumberList((prevList) => {
            return [...prevList, {id: uuidv4(), value: ""}];
          });
        }}
      >
        {strings.enterAnother}
      </Button>
      <Button
        to="/profile/transfer-in/review"
        state={{
          patientId:
            selectedPatientId === "" || selectedPatientId === DEFAULT_PATIENT_ID
              ? null
              : selectedPatientId,
          pharmacyName: pharmacyName,
          pharmacyPhone: formatPhoneNumber(pharmacyPhone),
          pharmacyCity: pharmacyCity,
          pharmacyState: pharmacyState,
          scriptOrDrug: [
            primaryRx,
            ...rxNumberList.map((rxNumber) => rxNumber.value),
          ].filter((rxNumber) => rxNumber.length > 0),
        }}
        disabled={disabled}
        className={styles.review}
      >
        {strings.review}
      </Button>
    </div>
  );
}
